import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import {
  EXACT_MANIFEST_SHA256,
  installExactMach1FromServer,
  openVerifiedMach1Installation,
  type VerifiedMach1Installation,
} from "./browser-mach1-store";
import { loadExactRuntimeBundle, type ExactRuntimeAssetsManifest } from "./exact-runtime-loader";
import {
  abortPhysicalServiceWorkerAudit,
  armPhysicalOfflineNavigation,
  createResumableSitePhysicalPhase1,
  disarmPhysicalServiceWorkerAudit,
  physicalServiceWorkerStatus,
  readCommittedSitePhysicalIdentity,
  resumeResumableSitePhysicalAcceptance,
  type CreatedResumablePhysicalPhase1,
  type ResumedSitePhysicalAcceptance,
} from "./physical-page-lifecycle";
import { buildVerifiedPhysicalReleaseExport } from "./physical-release-export";
import { exactPhysicalRunId } from "./physical-run-id";
import { sharedExactRuntimeOwner } from "./exclusive-runtime-owner.js";

const SHA256 = /^[0-9a-f]{64}$/u;
const HOST_INVOCATION_ID = /^host-[0-9a-f-]{16,128}$/u;
const NONCE = /^[0-9a-f]{32}$/u;
const AUTOMATION_ENTRY = "/runtime/site-automation/prime-mach1-physical-automation.js";
const RELEASE_INDEX_PATH = "/releases/prime-mach1-index.json";
const RELEASE_TREE_PREFIX = "/releases/";
const MODEL_TOTAL_BYTES = 7_872_753_504;

const modelIdentity = Object.freeze({
  manifestSha256: EXACT_MANIFEST_SHA256,
  tensorIndexSha256: "469206f990e5345e8670b2613d60f0ec007d6f7b180280d2922c8dec114e2383",
  unsplitSha256: "225c7f283c78e8990309cad9c9dddb17d928124cc071a82ec993e79880c03bb2",
  modelRevision: "bcbf113f7a9be509adc8fc9cbc49393a9f592485",
  machRevision: "fa68073841ae1b08b69194ec0371966352482e6c",
  totalByteCount: MODEL_TOTAL_BYTES,
  shardCount: 18,
  tensorCount: 1_637,
  architecture: "mach1" as const,
  formatVersion: 3 as const,
  fullModel: true as const,
});

const primeIdentity = Object.freeze({
  version: "0.7.1" as const,
  commit: "95afd319a78ae017a41241d50b013d656a0685ce",
  modelFacingTool: "ipython" as const,
  dynamicPythonSkills: true as const,
  persistentKernel: true as const,
  subagents: true as const,
  refinement: true as const,
});

type PhysicalState = "ready" | "phase1" | "armed" | "phase2" | "sealed" | "aborted";

interface PhysicalAdapterState {
  state: PhysicalState;
  activeRunId: string | null;
  nonce: string | null;
  descriptorSha256: string | null;
  navigationArmed: boolean;
  serviceWorkerAuditState: null | "collecting" | "sealed" | "aborted";
  installation: VerifiedMach1Installation | null;
  phase1: CreatedResumablePhysicalPhase1 | null;
  navigationCancel: (() => void) | null;
  resumed: ResumedSitePhysicalAcceptance | null;
}

const query = new URLSearchParams(location.search);
const queryNonce = query.get("physical_phase") === "2" ? query.get("nonce") : null;
const state: PhysicalAdapterState = {
  state: queryNonce && NONCE.test(queryNonce) ? "phase2" : "ready",
  activeRunId: null,
  nonce: queryNonce && NONCE.test(queryNonce) ? queryNonce : null,
  descriptorSha256: null,
  navigationArmed: queryNonce !== null,
  serviceWorkerAuditState: queryNonce !== null ? "collecting" : null,
  installation: null,
  phase1: null,
  navigationCancel: null,
  resumed: null,
};

async function withExclusiveRuntime<T>(
  label: string,
  operation: (lifecycle: { runtimeMayExist(): void; runtimeTerminal(): void }) => Promise<T>,
): Promise<T> {
  return await sharedExactRuntimeOwner.runFailClosed(label, operation);
}

function request(value: unknown, kind: string): Record<string, unknown> {
  if (!value || typeof value !== "object") throw new Error(`${kind} request is not an object`);
  const result = value as Record<string, unknown>;
  if (result.kind !== kind || result.schemaVersion !== 1) {
    throw new Error(`${kind} request has the wrong frozen identity`);
  }
  return result;
}

async function exactBytes(pathname: string): Promise<Uint8Array> {
  const response = await fetch(pathname, { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) throw new Error(`Frozen Site identity fetch failed: ${pathname} (${response.status})`);
  return new Uint8Array(await response.arrayBuffer());
}

function parseJson(bytes: Uint8Array, label: string): Record<string, unknown> {
  try {
    return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)) as Record<string, unknown>;
  } catch {
    throw new Error(`${label} is not canonical UTF-8 JSON`);
  }
}

function pageIdentity(value: {
  pageInstanceId: string;
  runtimeInstanceId: string;
  appInstanceId: string;
  modelLoadId: string;
}): Record<string, string> {
  return {
    pageInstanceId: value.pageInstanceId,
    runtimeInstanceId: value.runtimeInstanceId,
    appInstanceId: value.appInstanceId,
    modelLoadId: value.modelLoadId,
  };
}

function exactRef(
  value: unknown,
  pathPrefix: string,
  label: string,
): { path: string; byteCount: number; sha256: string } {
  const ref = value as { path?: unknown; byteCount?: unknown; sha256?: unknown };
  if (
    typeof ref?.path !== "string" || !ref.path.startsWith(pathPrefix) || ref.path.includes("..") ||
    !Number.isSafeInteger(ref.byteCount) || (ref.byteCount as number) <= 0 ||
    typeof ref.sha256 !== "string" || !SHA256.test(ref.sha256)
  ) throw new Error(`${label} is not an exact frozen asset reference`);
  return ref as { path: string; byteCount: number; sha256: string };
}

async function describeExecutable(): Promise<Record<string, unknown>> {
  const [runtimeBytes, appBytes, workerBytes] = await Promise.all([
    exactBytes("/runtime/runtime-assets.json"),
    exactBytes("/runtime/app-assets.json"),
    exactBytes("/sw.js"),
  ]);
  const runtime = parseJson(runtimeBytes, "runtime manifest") as unknown as ExactRuntimeAssetsManifest & Record<string, unknown>;
  const app = parseJson(appBytes, "application manifest") as {
    kind?: string;
    schemaVersion?: number;
    cacheBuildId?: string;
    serviceWorker?: unknown;
  };
  if (
    runtime.kind !== "mach1-browser-runtime-assets" || runtime.schemaVersion !== 2 ||
    runtime.qualified !== false || Object.hasOwn(runtime, "releaseBinding") ||
    app.kind !== "prime-mach1-offline-app-assets" || app.schemaVersion !== 2 ||
    !SHA256.test(app.cacheBuildId ?? "")
  ) throw new Error("Site executable manifests are not the immutable schema-2 candidate");
  const serviceWorker = exactRef(app.serviceWorker, "/sw.js", "service worker");
  const serviceWorkerValue = app.serviceWorker as typeof serviceWorker & { buildId?: string };
  if (
    serviceWorker.path !== "/sw.js" || serviceWorkerValue.buildId !== app.cacheBuildId ||
    workerBytes.byteLength !== serviceWorker.byteCount || bytesToHex(sha256(workerBytes)) !== serviceWorker.sha256
  ) throw new Error("Service worker bytes are not bound by the immutable application manifest");

  const automation = exactRef(runtime.siteAutomation?.entry, "/runtime/site-automation/", "Site automation adapter");
  const automationManifest = exactRef(runtime.siteAutomation?.manifest, "/runtime/site-automation/", "Site automation manifest");
  const browserManifest = exactRef(runtime.browserApp?.manifest, "/runtime/exact-app/", "browser app manifest");
  const browserEntry = exactRef(runtime.browserApp?.entry, "/runtime/exact-app/", "browser app entry");
  const verifierManifest = exactRef(runtime.physicalVerifier?.manifest, "/runtime/physical-verifier/", "physical verifier manifest");
  const verifierEntry = exactRef(runtime.physicalVerifier?.entry, "/runtime/physical-verifier/", "physical verifier entry");
  const physicalCoreEntry = exactRef(runtime.physicalCore?.entry, "/runtime/exact-app/", "resumable physical core");
  if (
    automation.path !== AUTOMATION_ENTRY || runtime.siteAutomation.frozen !== true ||
    runtime.physicalCore.frozen !== true ||
    physicalCoreEntry.sha256 !== browserEntry.sha256
  ) throw new Error("Site adapter or resumable physical core is not frozen");
  const [
    automationBytes,
    automationManifestBytes,
    browserManifestBytes,
    browserEntryBytes,
    verifierManifestBytes,
    verifierEntryBytes,
  ] = await Promise.all([
    exactBytes(automation.path),
    exactBytes(automationManifest.path),
    exactBytes(browserManifest.path),
    exactBytes(browserEntry.path),
    exactBytes(verifierManifest.path),
    exactBytes(verifierEntry.path),
  ]);
  for (const [bytes, ref, label] of [
    [automationBytes, automation, "Site automation adapter"],
    [automationManifestBytes, automationManifest, "Site automation manifest"],
    [browserManifestBytes, browserManifest, "browser app manifest"],
    [browserEntryBytes, browserEntry, "browser app entry"],
    [verifierManifestBytes, verifierManifest, "physical verifier manifest"],
    [verifierEntryBytes, verifierEntry, "physical verifier entry"],
  ] as const) {
    if (bytes.byteLength !== ref.byteCount || bytesToHex(sha256(bytes)) !== ref.sha256) {
      throw new Error(`${label} bytes changed after staging`);
    }
  }
  return {
    serviceWorkerBuildId: app.cacheBuildId,
    serviceWorkerScriptSha256: serviceWorker.sha256,
    serviceWorkerScriptByteCount: serviceWorker.byteCount,
    appManifestSha256: bytesToHex(sha256(appBytes)),
    appManifestByteCount: appBytes.byteLength,
    runtimeManifestSha256: bytesToHex(sha256(runtimeBytes)),
    runtimeManifestByteCount: runtimeBytes.byteLength,
    runtimeManifestQualified: false,
    runtimeManifestReleaseBindingPresent: false,
    browserAppManifestSha256: browserManifest.sha256,
    browserAppEntrySha256: browserEntry.sha256,
    physicalVerifierManifestSha256: verifierManifest.sha256,
    physicalVerifierBuildSha256: verifierEntry.sha256,
    resumableCoreEntrySha256: physicalCoreEntry.sha256,
    automationAdapterSha256: automation.sha256,
    resumableCoreFrozen: true,
    automationAdapterFrozen: true,
    publicationBoundary: "append-only-release-index",
    releaseIndexPath: RELEASE_INDEX_PATH,
    releaseTreePrefix: RELEASE_TREE_PREFIX,
    releaseIndexExcludedFromOfflineCache: true,
    releasePublicationMutatesExecutable: false,
  };
}

async function describe(): Promise<Record<string, unknown>> {
  return {
    kind: "prime-mach1-site-physical-automation",
    schemaVersion: 1,
    syntheticFixturesCanProduceReceipt: false,
    hardcodedTools: false,
    model: structuredClone(modelIdentity),
    primeAgent: structuredClone(primeIdentity),
    site: await describeExecutable(),
    capabilities: [
      "physical-status",
      "install-or-open-exact-model",
      "run-resumable-phase1",
      "arm-offline-navigation",
      "resume-resumable-phase2",
      "export-verified-evidence",
      "abort-physical-run",
      "disarm-physical-navigation",
    ],
  };
}

async function getPhysicalStatus(value: unknown): Promise<Record<string, unknown>> {
  request(value, "prime-mach1-host-status-request");
  const active = await physicalServiceWorkerStatus().catch(() => null);
  if (active) {
    state.nonce = active.marker.nonce;
    state.descriptorSha256 = active.marker.descriptorSha256;
    state.navigationArmed = true;
    state.serviceWorkerAuditState = active.terminal === "active" ? "collecting" : active.terminal;
    if (active.terminal === "aborted") state.state = "aborted";
    else if (active.terminal === "sealed") state.state = "sealed";
    else if (query.get("physical_phase") === "2") state.state = "phase2";
    else state.state = "armed";
  }
  if (!state.activeRunId && state.nonce) {
    const committed = await readCommittedSitePhysicalIdentity(state.nonce);
    state.activeRunId = committed.descriptor.runId;
    state.descriptorSha256 = committed.descriptorSha256;
  }
  return {
    kind: "prime-mach1-site-physical-status",
    schemaVersion: 1,
    state: state.state,
    activeRunId: state.activeRunId,
    resumableCoreFrozen: true,
    automationAdapterFrozen: true,
    navigationArmed: state.navigationArmed,
    serviceWorkerAuditState: state.serviceWorkerAuditState,
  };
}

async function installOrOpenExactModel(value: unknown): Promise<Record<string, unknown>> {
  const input = request(value, "prime-mach1-host-install-request");
  if (
    input.manifestUrl !== "/model/manifest.json" ||
    input.shardUrlTemplate !== "/api/model/shards/{encodedFileName}" ||
    input.manifestSha256 !== EXACT_MANIFEST_SHA256 ||
    input.totalByteCount !== MODEL_TOTAL_BYTES || input.shardCount !== 18 ||
    input.allowMissingShardDownloads !== true || input.allowDuplicateCopies !== false
  ) throw new Error("Host install request does not authorize the exact single-copy MACH-1 installation");
  const installation = await installExactMach1FromServer();
  state.installation = installation;
  return {
    kind: "prime-mach1-exact-opfs-installation",
    schemaVersion: 1,
    manifestSha256: installation.manifestSha256,
    totalByteCount: installation.manifest.split.totalByteCount,
    shardCount: installation.files.length,
    opfsRoot: `prime-mach1/${installation.manifestSha256}`,
    everyShardSha256Verified: true,
    duplicateCopyByteCount: installation.accounting.duplicateCopyByteCount,
    downloadedByteCount: installation.accounting.downloadedByteCount,
    reusedVerifiedByteCount: installation.accounting.reusedVerifiedByteCount,
    verifiedFileNames: [...installation.accounting.verifiedFileNames],
  };
}

function phase1Scenario(result: CreatedResumablePhysicalPhase1): Record<string, unknown> {
  const acceptance = result.acceptance as unknown as {
    scenario?: {
      children?: unknown[];
      dynamicSkill?: { sourceSha256?: string; laterUseResultSha256?: string };
      refinement?: {
        stateBeforeSha256?: string;
        stateAfterApplySha256?: string;
        stateAfterRollbackSha256?: string;
      };
    };
    decoderProbes?: {
      isolation?: {
        firstA?: string;
        b?: string;
        secondA?: string;
        firstAResult?: { terminalText?: string };
        bResult?: { terminalText?: string };
        secondAResult?: { terminalText?: string };
      };
      abortRecovery?: {
        aborted?: string;
        recoveredC?: string;
        recoveredCResult?: { terminalText?: string };
      };
    };
  };
  const scenario = acceptance.scenario;
  const probes = acceptance.decoderProbes;
  return {
    exactMultiTurn: true,
    dynamicSkillBuiltAndUsed: SHA256.test(scenario?.dynamicSkill?.sourceSha256 ?? "") &&
      SHA256.test(scenario?.dynamicSkill?.laterUseResultSha256 ?? ""),
    subagentCount: scenario?.children?.length ?? 0,
    refinementAppliedAndRolledBack: [
      scenario?.refinement?.stateBeforeSha256,
      scenario?.refinement?.stateAfterApplySha256,
      scenario?.refinement?.stateAfterRollbackSha256,
    ].every((hash) => SHA256.test(hash ?? "")),
    decoderIsolationABA: Boolean(
      probes?.isolation?.firstA && probes.isolation.b && probes.isolation.secondA &&
      probes.isolation.firstAResult?.terminalText === "A" &&
      probes.isolation.bResult?.terminalText === "B" &&
      probes.isolation.secondAResult?.terminalText === "A",
    ),
    abortRecoveryC: Boolean(
      probes?.abortRecovery?.aborted && probes.abortRecovery.recoveredC &&
      probes.abortRecovery.recoveredCResult?.terminalText === "C",
    ),
  };
}

async function runResumablePhase1(value: unknown): Promise<Record<string, unknown>> {
  return await withExclusiveRuntime("resumable physical phase one", async (lifecycle) => {
  const input = request(value, "prime-mach1-host-phase1-request");
  if (
    typeof input.hostInvocationId !== "string" || !HOST_INVOCATION_ID.test(input.hostInvocationId) ||
    input.syntheticFixturesCanProduceReceipt !== false
  ) throw new Error("Host phase-one request is not authorized for a physical run");
  const installation = state.installation ?? await openVerifiedMach1Installation();
  const bundle = await loadExactRuntimeBundle(installation);
  lifecycle.runtimeMayExist();
  const result = await createResumableSitePhysicalPhase1(installation, bundle, input.hostInvocationId);
  lifecycle.runtimeTerminal();
  const checkpoint = result.acceptance.exactCheckpoint;
  state.installation = installation;
  state.phase1 = result;
  state.state = "phase1";
  state.activeRunId = result.descriptor.runId;
  state.nonce = result.descriptor.nonce;
  state.descriptorSha256 = result.descriptorSha256;
  state.navigationArmed = false;
  state.serviceWorkerAuditState = null;
  return {
    kind: "prime-mach1-site-host-phase1",
    schemaVersion: 1,
    hostInvocationId: input.hostInvocationId,
    runId: result.descriptor.runId,
    coreRunIdSource: "createResumablePhysicalPhase1",
    resumableCoreEnvelopeKind: result.envelope.kind,
    resumeNonce: result.descriptor.nonce,
    siteDescriptorSha256: result.descriptorSha256,
    resumableCoreEnvelopeSha256: result.envelope.descriptorSha256,
    resumableCoreEnvelopeByteCount: result.envelope.descriptorByteCount,
    siteDescriptorByteCount: result.descriptorBytes.byteLength,
    descriptorPersistedToOpfs: true,
    descriptorCommitMarkerWrittenLast: true,
    crossOriginIsolated: result.descriptor.page.crossOriginIsolated,
    navigatorOnline: result.descriptor.page.navigatorOnline,
    serviceWorkerControlled: result.descriptor.page.serviceWorker.controlled,
    modelFilesPassedDirectlyFromVerifiedOpfs: true,
    model: structuredClone(modelIdentity),
    primeAgent: structuredClone(primeIdentity),
    phase1: pageIdentity(checkpoint),
    scenario: phase1Scenario(result),
  };
  });
}

function assertRunRequest(input: Record<string, unknown>): void {
  if (
    typeof input.runId !== "string" || exactPhysicalRunId(input.runId) !== input.runId ||
    typeof input.resumeNonce !== "string" || !NONCE.test(input.resumeNonce) ||
    typeof input.siteDescriptorSha256 !== "string" || !SHA256.test(input.siteDescriptorSha256)
  ) throw new Error("Host physical request has an invalid run/nonce/descriptor identity");
}

async function armOfflineNavigation(value: unknown): Promise<Record<string, unknown>> {
  const input = request(value, "prime-mach1-host-offline-arm-request");
  assertRunRequest(input);
  const committed = state.phase1;
  if (
    !committed || input.runId !== committed.descriptor.runId ||
    input.resumeNonce !== committed.descriptor.nonce ||
    input.siteDescriptorSha256 !== committed.descriptorSha256
  ) throw new Error("Offline arm request does not match the in-memory and OPFS phase-one descriptor");
  const armed = await armPhysicalOfflineNavigation(committed);
  state.navigationCancel = armed.cancel;
  state.state = "armed";
  state.navigationArmed = true;
  state.serviceWorkerAuditState = "collecting";
  return {
    kind: "prime-mach1-site-offline-navigation-armed",
    schemaVersion: 1,
    runId: committed.descriptor.runId,
    resumeNonce: committed.descriptor.nonce,
    siteDescriptorSha256: committed.descriptorSha256,
    armed: true,
    offlineEventListenerInstalled: true,
    pagehideListenerInstalled: true,
    originLockdownArmed: true,
    navigationUrl: armed.navigationUrl,
  };
}

async function resumeResumablePhase2(value: unknown): Promise<Record<string, unknown>> {
  return await withExclusiveRuntime("resumable physical phase two", async (lifecycle) => {
  const input = request(value, "prime-mach1-host-phase2-request");
  assertRunRequest(input);
  if (
    input.browserContextOffline !== true || typeof input.networkDisabledAt !== "string" ||
    Number.isNaN(Date.parse(input.networkDisabledAt)) || navigator.onLine !== false ||
    input.resumeNonce !== state.nonce
  ) throw new Error("Host phase-two request is not bound to this disconnected nonce page");
  state.activeRunId = input.runId as string;
  state.descriptorSha256 = input.siteDescriptorSha256 as string;
  state.state = "phase2";
  state.navigationArmed = true;
  state.serviceWorkerAuditState = "collecting";
  lifecycle.runtimeMayExist();
  const resumed = await resumeResumableSitePhysicalAcceptance(
    input.resumeNonce as string,
    undefined,
    { runId: input.runId as string, descriptorSha256: input.siteDescriptorSha256 as string },
  );
  lifecycle.runtimeTerminal();
  state.resumed = resumed;
  state.state = "sealed";
  state.navigationArmed = false;
  state.serviceWorkerAuditState = "sealed";
  const phase2 = resumed.phase2;
  const physical = resumed.result.phase2;
  return {
    kind: "prime-mach1-site-host-phase2",
    schemaVersion: 1,
    runId: resumed.committed.descriptor.runId,
    resumableCoreResultSource: "resumeResumablePhysicalAcceptance",
    resumeNonce: resumed.committed.descriptor.nonce,
    siteDescriptorSha256: resumed.committed.descriptorSha256,
    crossOriginIsolated: resumed.page.crossOriginIsolated,
    navigatorOnline: resumed.page.navigatorOnline,
    serviceWorkerControlledAtDocumentStart: resumed.page.documentStart?.serviceWorkerController != null,
    network: {
      browserContextOffline: true,
      networkDisabledAt: input.networkDisabledAt,
      successfulNetworkRequestsAfterOffline: 0,
    },
    opfs: {
      filesReopenedAfterNavigation: true,
      fileCount: resumed.committed.descriptor.modelFileInventory.length,
      fileNames: resumed.committed.descriptor.modelFileInventory.map((file) => file.name),
      everyShardRehashed: true,
      manifestRehashed: true,
      tensorIndexRehashed: true,
    },
    scenario: {
      offlineMach1TurnCompleted: physical.recoveredResult.length > 0,
      persistedKernelFilesRecovered: physical.recoveredFileValue.length > 0,
      dynamicSkillRecoveredAndUsed: physical.recoveredResult.length > 0,
    },
    serviceWorkerAudit: {
      sealed: true,
      byteCount: resumed.result.navigation.serviceWorkerAuditByteCount,
      sha256: resumed.result.navigation.serviceWorkerAuditSha256,
    },
    controls: { abortAvailable: true, disarmAvailable: true },
    release: {
      committedToOpfs: true,
      releaseMarkerWrittenLast: true,
      independentlyVerifiedInWorker: true,
      runId: resumed.result.bundle.runId,
      verifierBuildSha256: resumed.result.bundle.release.verification.verifierBuildSha256,
    },
    model: structuredClone(modelIdentity),
    primeAgent: structuredClone(primeIdentity),
    phase2: pageIdentity({
      pageInstanceId: phase2.pageInstanceId,
      runtimeInstanceId: phase2.runtimeInstanceId,
      appInstanceId: phase2.appInstanceId,
      modelLoadId: phase2.modelLoadId,
    }),
  };
  });
}

async function exportVerifiedEvidence(value: unknown): Promise<Record<string, unknown>> {
  const input = request(value, "prime-mach1-host-evidence-export-request");
  assertRunRequest(input);
  if (
    input.syntheticFixturesCanProduceReceipt !== false ||
    input.runId !== state.activeRunId || input.resumeNonce !== state.nonce ||
    input.siteDescriptorSha256 !== state.descriptorSha256
  ) throw new Error("Evidence export does not match the sealed physical run");
  const hostNetwork = input.hostNetworkObservation as {
    networkDisabledAt?: unknown;
    successfulNetworkRequests?: unknown;
  };
  if (
    typeof hostNetwork?.networkDisabledAt !== "string" || Number.isNaN(Date.parse(hostNetwork.networkDisabledAt)) ||
    hostNetwork.successfulNetworkRequests !== 0
  ) throw new Error("Host network observation did not remain physically offline");
  const exported = await buildVerifiedPhysicalReleaseExport(input.runId as string);
  state.state = "sealed";
  state.navigationArmed = false;
  state.serviceWorkerAuditState = "sealed";
  return {
    ...exported,
    publicationBoundary: "append-only-release-index",
    releaseIndexPath: RELEASE_INDEX_PATH,
  };
}

async function disarmPhysicalNavigation(value: unknown): Promise<Record<string, unknown>> {
  const input = request(value, "prime-mach1-host-disarm-request");
  assertRunRequest(input);
  if (state.activeRunId && input.runId !== state.activeRunId) {
    throw new Error("Disarm request targets a different physical run");
  }
  state.navigationCancel?.();
  state.navigationCancel = null;
  const active = await physicalServiceWorkerStatus().catch(() => null);
  if (active && active.marker.nonce === input.resumeNonce && active.terminal === "sealed") {
    await disarmPhysicalServiceWorkerAudit(input.resumeNonce as string);
  }
  if (!active || active.terminal !== "active") state.navigationArmed = false;
  return {
    kind: "prime-mach1-site-physical-control-ack",
    schemaVersion: 1,
    operation: "disarm",
    runId: input.runId,
    accepted: true,
  };
}

async function abortPhysicalRun(value: unknown): Promise<Record<string, unknown>> {
  const input = request(value, "prime-mach1-host-abort-request");
  if (typeof input.runId !== "string" || exactPhysicalRunId(input.runId) !== input.runId) {
    throw new Error("Abort request run ID is invalid");
  }
  if (state.activeRunId && input.runId !== state.activeRunId) {
    throw new Error("Abort request targets a different physical run");
  }
  const reason = typeof input.reason === "string" && input.reason.trim().length > 0
    ? input.reason.trim()
    : "host_requested_abort";
  state.navigationCancel?.();
  state.navigationCancel = null;
  const active = await physicalServiceWorkerStatus().catch(() => null);
  if (active?.terminal === "active") {
    await abortPhysicalServiceWorkerAudit(active.marker.nonce, reason);
    state.serviceWorkerAuditState = "aborted";
    state.navigationArmed = false;
    state.state = "aborted";
  } else if (active?.terminal === "aborted") {
    state.serviceWorkerAuditState = "aborted";
    state.navigationArmed = false;
    state.state = "aborted";
  }
  return {
    kind: "prime-mach1-site-physical-control-ack",
    schemaVersion: 1,
    operation: "abort",
    runId: input.runId,
    accepted: true,
  };
}

const automation = Object.freeze({
  kind: "prime-mach1-site-physical-automation" as const,
  schemaVersion: 1 as const,
  describe,
  getPhysicalStatus,
  installOrOpenExactModel,
  runResumablePhase1,
  armOfflineNavigation,
  resumeResumablePhase2,
  exportVerifiedEvidence,
  disarmPhysicalNavigation,
  abortPhysicalRun,
});

Object.defineProperty(globalThis, "__PRIME_MACH1_PHYSICAL_V1__", {
  configurable: false,
  enumerable: false,
  writable: false,
  value: automation,
});

declare global {
  var __PRIME_MACH1_PHYSICAL_V1__: typeof automation;
}

export { automation as PRIME_MACH1_PHYSICAL_V1 };
